<template>
  <div>
    <div v-if="loading" class="w-full h-full flex justify-center items-center loading">
      <p class="text-gray-400 font-bold animate-pulse">Searching...</p>
    </div>
    <div v-else-if="saving" class="w-full h-full flex justify-center items-center loading">
      <p class="text-gray-400 font-bold animate-pulse">Saving...</p>
    </div>

    <PageHeader :title="isSmallDevice?'':'メイン一覧'" class="list">
      <template #title-header-content>
        <div class="flex-1 pl-2 flex justify-end items-center">

          <div class="flex">
            <div class="flex flex-wrap">
              <span class="text-gray-100 font-bold text-xs">サブタスク</span>
              <Toggle v-model="toggle.withSub" size="small" class="pl-2"/>
            </div>

            <div class="ml-4 flex flex-wrap">
              <span class="text-gray-100 font-bold text-xs">メイン</span>
              <Toggle v-model="toggle.mainTask" size="small" class="pl-2"/>
            </div>

            <div class="ml-4 flex flex-wrap">
              <span class="text-gray-100 font-bold text-xs">受注</span>
              <Toggle v-model="toggle.orderTask" size="small" class="pl-2"/>
            </div>

            <div class="ml-4 flex flex-wrap">
              <span class="text-gray-100 font-bold text-xs">DC雇入</span>
              <Toggle v-model="toggle.employmentTask" size="small" class="pl-2"/>
            </div>

            <div v-if="isRegularStaffAuthorized" class="ml-4 flex flex-wrap" v-tooltip="'管理者・人事部以外は同じ部署のみ表示します'">
              <span class="text-gray-100 font-bold text-xs">社員雇入</span>
              <Toggle v-model="toggle.employmentRegularTask" size="small" class="pl-2"/>
            </div>
          </div>

          <div class="flex btn-form ml-4">
            <!-- 担当者絞込み -->
            <div class="mx-1">
              <PrimaryButton
                v-tooltip="'自分の担当と未定分'"
                text="担当分・未定"
                :buttonContents="'要'"
                :class="btn.user ? 'on' : 'off'"
                :size="isSmallDevice?'xs':'normal px-2'"
                class="whitespace-nowrap"
                @click="btn.user=!btn.user">
              </PrimaryButton>
            </div>

            <!-- 依頼主絞込み -->
            <div class="mx-1">
              <PrimaryButton
                v-tooltip="'自分が依頼者'"
                text="依頼分"
                :buttonContents="'要'"
                :class="btn.byUser ? 'on' : 'off'"
                :size="isSmallDevice?'xs':'normal px-2'"
                class="whitespace-nowrap"
                @click="btn.byUser=!btn.byUser">
              </PrimaryButton>
            </div>

            <!-- 本日までの期限ボタン -->
            <div v-if="isSmallDevice" class="mx-1">
              <PrimaryButton
                :buttonContents="'要'"
                text="本日まで"
                :class="btn.untilToday ? 'on' : 'off'"
                size="xs whitespace-nowrap"
                @click="untilToday()">
              </PrimaryButton>
            </div>
            <div v-else class="mx-1">
              <PrimaryButton
                v-tooltip="'本日まで'"
                :buttonContents="'要'"
                :class="btn.untilToday ? 'on' : 'off'"
                size="normal px-2"
                @click="untilToday()">
                <Icon slot="before" iconName="Clock" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
              </PrimaryButton>
            </div>

            <!-- 未完了のみボタン -->
            <div v-if="isSmallDevice" class="mx-1">
              <PrimaryButton
                text="未完了"
                :buttonContents="'要'"
                :class="btn.incomplete ? 'on' : 'off'"
                size="xs whitespace-nowrap"
                @click="btn.incomplete=!btn.incomplete">
              </PrimaryButton>
            </div>
            <div v-else class="mx-1">
              <PrimaryButton
                v-tooltip="'未完了のみ'"
                :buttonContents="'要'"
                :class="btn.incomplete ? 'on' : 'off'"
                class="px-2"
                @click="btn.incomplete=!btn.incomplete">
                <Icon slot="before" iconName="ExclamationCircle" iconType="solid" :strokeWidth="2" class="h-5 w-5" />
              </PrimaryButton>
            </div>

            <div class="mx-5">
              <PrimaryButton text="検索" @click="search()" class="min-w-100px" :buttonContents="'要'" />
            </div>

            <div class="mx-1">
              <PrimaryButton text="クリア" :size="isSmallDevice?'xs':'normal'" class="off px-1 whitespace-nowrap" @click="clear" :buttonContents="'要'" />
            </div>

            <div v-if="!isSmallDevice" class="mx-1">
              <vue-json-to-csv :json-data="searchResults" :csv-title="'メインタスク一覧'" :labels="labels">
                <PrimaryButton text="CSV出力" class="bg-yellow-600 px-1 whitespace-nowrap" />
              </vue-json-to-csv>
            </div>

            <PrimaryButton text="カレンダー" class="mx-1 bg-blue-100 text-light-blue-700 border-light-blue-700 px-2 whitespace-nowrap text-xs" @click="$router.push({ name: 'TaskCalendar' })">
              <Icon slot="before" iconName="Calendar" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
            </PrimaryButton>

            <div class="mx-1">
              <router-link :to="{ name: 'RequestPost' }" class="ml-2">
                <PrimaryButton text="新規登録" class="min-w-100px bg-green-600 px-2">
                  <Icon v-if="!isSmallDevice" slot="before" iconName="PencilAlt" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
                </PrimaryButton>
              </router-link>
            </div>

            <Icon
              iconName="Refresh"
              :clickable="false"
              :class="{'animate-spin': loading}"
              class="text-white hover:text-gray-300 active:text-gray-400 ml-4 cursor-pointer self-center"
              @click.native="refresh"
            />
          </div>
        </div>
      </template>
      <template #page-header-content>
        <div class="flex flex-1 justify-end">

          <div class="text-light-blue-100 font-bold text-sm mr-auto mt-2 cursor-pointer whitespace-nowrap" @click="clickKeword()">
            <span v-if="keywordOpen" class="text-base">▼</span>
            <span v-else class="text-base">▶</span>
            <span class="pl-2">キーワード検索</span>
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <WmsSuggestInput
              name="category"
              :dynamic="false"
              :incrementalSearch="false"
              :selectionItems="categoryList"
              displayField="text"
              placeholder="お問合わせカテゴリー"
              v-model="text.category"
              :clearable="true"
              class="sug p-0.5 border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.category?'yellow':''"
              :embed="true"
              id="category"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.title"
              class="title w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.title?'bg-yellow-200 font-bold':''"
              placeholder="タイトル / デンタツ問合わせ内容"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.clientName"
              class="title min-w-200 py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.clientName?'bg-yellow-200 font-bold':''"
              placeholder="お客様会社名 / デンタツ物件名"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.clientPersonName"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.clientPersonName?'bg-yellow-200 font-bold':''"
              placeholder="お客様担当者名"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <WmsSuggestInput
              name="toDepartment"
              :dynamic="false"
              :incrementalSearch="false"
              :filter="{}"
              :selectionItems="toDepartmentList"
              displayField="name"
              placeholder="依頼先部署"
              v-model="text.toDepartment"
              :clearable="true"
              class="sug p-0.5 border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.toDepartment?'yellow':''"
              :embed="true"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.toStaffName"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.toStaffName?'bg-yellow-200 font-bold':''"
              placeholder="メイン担当者"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.byUser"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:bg-white focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.byUser?'bg-yellow-200 font-bold':''"
              placeholder="依頼主"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <DateSelect
              class="filter-date"
              ref="dateSelect"
              :presetName="text.limitPeriodName"
              :period="text.limitPeriod"
              :none="true"
              :titleText="text.limitPeriod&&text.limitPeriod.start?'':'期限日'"
              :class="text.limitPeriod&&text.limitPeriod.start?'yellow':''"
              width="200px"
              @change="periodChange"
            />
          </div>

          <div class="search-contents relative inline-block text-sm" style="height: 41px;">
            <span v-if="!text.kubunLimit" class="absolute left-2 top-2.5 text-sm text-gray-500 pointer-events-none">
              短期・長期
            </span>

            <select v-model="text.kubunLimit" class="allow-none border-gray-300 rounded-md py-1.5" :class="text.kubunLimit?'font-bold bg-yellow-200':''">
              <option :value="null"></option>
              <option :value="1">短期</option>
              <option :value="2">長期</option>
            </select>
          </div>
        </div>
        <div v-if="keywordOpen||text.searchKeyword" class="mt-2">
          <input
            type="search"
            v-model="text.searchKeyword"
            class="w-full bg-light-blue-600 bg-opacity-20 py-2 pl-10 pr-3 border border-light-blue-300 rounded-md leading-5 keyword placeholder-light-blue-200 sm:text-sm text-light-blue-100  font-bold"
            placeholder="キーワード検索（お問い合わせ番号 ・ お問い合わせ内容 ・ 対応内容 ・ メールアドレス ・ デンタツ電話番号 ・ フリガナ（面接、雇入） から部分一致で検索）スペースでAND検索します。"
          />
        </div>
      </template>
    </PageHeader>

    <main class="absolute w-full bottom-4 -mt-2" :class="keywordOpen||text.searchKeyword?'top-43':'top-32'">
      <div class="mx-auto px-2 sm:px-4 lg:px-8 h-full">
        <div class="bg-white rounded-md border border-gray-200 ---overflow-auto h-full table-width">

          <div v-if="searchResults && searchResults.length" class="h-full overflow-y-auto" ref="requestPostList" @scroll="onScroll()">

            <table class="w-full overflow-auto">
              <thead class="sticky top-0 t-thead">
                <tr v-if="searchResults && searchResults.length" class="">
                  <th v-for="(col, idx) in columns" :key="idx" scope="col" class="px-2 py-3 text-xs text-gray-600 tracking-wider whitespace-nowrap cursor-pointer" :class="col.class" @click="sortSwitching(col)" v-tooltip="sortMsg(col)">
                    {{ col.title }}
                    <span v-if="text.sortItem==col.item">
                      <Icon iconName="ArrowDown" :class="text.sortBy==1?'text-blue-700':'text-gray-400'" class="w-3 h-4 mr-0.5 inline"/>
                      <Icon iconName="ArrowUp" :class="text.sortBy==-1?'text-blue-700':'text-gray-400'" class="w-3 h-4 mr-0.5 inline"/>
                    </span>
                    <span v-else-if="col.title">
                      <Icon iconName="ArrowDown" class="w-3 h-4 mr-0.5 inline text-gray-400"/>
                      <Icon iconName="ArrowUp" class="w-3 h-4 mr-0.5 inline text-gray-400"/>
                    </span>
                  </th>
                </tr>
              </thead>

              <tr v-for="(row, idx) in searchResults" :key="idx" class="tracking-wider text-sm hover-color cursor-pointer text-gray-700 border-b border-white" :class="row.color" @dblclick="toEditMain(row)" v-tooltip="requestNo(row)">
                <!-- 操作 -->
                <td class="row text-center">
                  <div v-if="[mainType, 'order'].includes(row.type)" @click="changeStatus(row)" class="blue-text whitespace-nowrap">{{ row.statusText }}</div>
                  <div v-else class="text-gray-500 text-xss whitespace-nowrap">(サブタスク)</div>
                </td>
                <!-- ステータスアイコン -->
                <td class="row text-center">
                  <Icon :iconName="row.iconName" :class="row.iconColor" />
                </td>
                <!-- お問い合わせカテゴリー -->
                <td class="row">
                  <div class="truncate min-w-135px max-w-216px">{{ row.categoryString }}</div>
                </td>
                <!-- タイトル -->
                <td class="row">
                  <div class="min-w-100px">{{ row.inquiry_title }}</div>
                </td>
                <!-- お客様会社名 -->
                <td class="row">
                  <div class="truncate w-48">{{ row.clientName }}</div>
                </td>
                <!-- お客様担当者名 -->
                <td class="row">
                  <div class="truncate w-32">{{ row.clientPersonName }}</div>
                </td>
                <!-- 担当者 -->
                <td class="row">
                  <div class="truncate w-32">{{ row.toStaffName }}</div>
                </td>
                <!-- 期限 -->
                <td class="row-limit text-center">
                  <div class="truncate whitespace-nowrap text-xs">{{ row.limitDate }}</div>
                  <div :class="row.kubunLimitColor">{{ row.kubunLimitString }}</div>
                </td>
                <!-- 複写 -->
                <td class="row blue-text">
                  <!-- メインタスクの場合のみ複写 -->
                  <div v-if="row.type==mainType" @click="toCopyMain(row._id)" class="whitespace-nowrap">複写</div>
                </td>
                <!-- 編集 -->
                <td class="row blue-text">
                  <div v-if="row.status=='open'&&[mainType, 'order'].includes(row.type)" @click="toEditMain(row)" class="whitespace-nowrap">編集</div>
                </td>
                <!-- 依頼登録 -->
                <td class="row">
                  <div v-if="row.status=='open'&&row.temporarySaveFlag" class="whitespace-nowrap">仮登録</div>
                  <div v-else-if="row.status=='open'&&[mainType, 'order'].includes(row.type)" @click="toSub(row._id, row.type)" class="blue-text whitespace-nowrap">依頼登録</div>
                </td>
              </tr>
            </table>
          </div>

          <div class="text-transparent text-xs">{{reload}}</div>

          <div v-if="!loading && searchResults.length === 0" class="w-full h-full flex justify-center items-center text-gray-400">
            <EmptyMessage
              iconName="Document"
              message="該当する案件はありません。"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import EmptyMessage from '@components/EmptyMessage.vue'
import VueJsonToCsv from 'vue-json-to-csv'
import Toggle from '@components/Toggle.vue'
import DateSelect from '@components/DateSelect.vue'
import { categorySelections } from '@libs/constants'
import * as requestPostManager from '@managers/requestPostManager'
import * as taskRequestManager from '@managers/taskRequestManager'
import * as orderManager from '@managers/orderManager'
import * as logManager from '@managers/logManager'
import * as estimateManager from '@managers/estimateManager'
import * as utils from '@libs/utils'
import * as dialogs from '@libs/dialogs'
import _ from 'lodash'

import PageModel from '../../main/pages/PageModel.vue'

// ボタン検索セッションストレージ（メニューへ行ってもリセットされない）
const stBtn = 'requestPostListBtn'
// トグル検索セッションストレージ（メニューへ行ってもリセットされない）
const stToggle = 'requestPostListToggle'
// 文字列検索セッションストレージ（メニューへ行くとリセット）
const stText = 'requestPostListText'
// 取得した最後の作成日、問い合わせNo（カーソルが下にきた場合に、このキーの次から取得。しおりとなる）
const stLast = 'requestPostListLast'

// 検索対象プロパティ
const searchKeys = [
  'requestNo',
  'clientMail',
  'note',
  'reason_note',
  'telephoneNumber',
  'interviewsKana',
  'employmentKana'
]

export default {
  extends: PageModel,
  components: {
    Icon,
    PrimaryButton,
    PageHeader,
    EmptyMessage,
    VueJsonToCsv,
    Toggle,
    WmsSuggestInput,
    DateSelect,
  },

  data() {
    return {
      // 一覧データ
      searchResults: [],
      loading: false,
      saving: false,
      freeze: true,
      firstRefresh: true,
      reload: 0,
      // キーワード検索open
      keywordOpen: false,
      // 取得したデータの最後の行のソート項目
      last: {
        scrollEnd: false
      },
      // 社員雇い入れ切り替えタブ表示
      isRegularStaffAuthorized: false,
      // ボタン検索条件（メニュー画面に戻ってもリセットされない）
      btn: {
        // 自分の担当
        user: true,
        // 自分の依頼
        byUser: true,
        // 本日までの期限
        untilToday: false,
        // 未完了検索
        incomplete: true,
      },
      // トグル検索条件（メニュー画面に戻ってもリセットされない）
      toggle: {
        // サブタスク表示
        withSub: false,
        // メインタスク
        mainTask: true,
        // 受注タスク
        orderTask: false,
        // DC雇い入れ
        employmentTask: false,
        // 社員雇い入れ
        employmentRegularTask: false,
      },
      // 検索欄記入（メニュー画面に戻るとリセット）
      text: {
        // フリー検索キーワード
        searchKeyword: '',
        // タイトル
        title: '',
        // お問い合わせカテゴリー
        category: '',
        // 得意先名
        clientName: '',
        // 得意先担当者
        clientPersonName: '',
        // 部署
        toDepartment: '',
        // 担当者
        toStaffName: '',
        // 依頼主
        byUser: '',
        // 期限
        limitPeriodName:  'custom',
        limitPeriod: {},
        // 期限区分
        kubunLimit: null,
        // ソート対象項目
        sortItem : 'createDate',
        // 昇順降順（初期値：降順）
        sortBy: -1
      },
      // お問い合わせカテゴリーリスト
      categoryList: categorySelections,
      // 部署リスト
      toDepartmentList: [],
      // CSVラベル
      labels: {
        category2: { title : "社内/お客様" },
        category: { title : "お問い合わせカテゴリー" },
        requestNo: { title : "お問い合わせ番号(登録時)" },
        clientName: { title : "お客様会社名" },
        clientPersonName: { title : "お客様担当者名" },
        clientMail: { title : "メールアドレス(返信メールが届きます)" },
        inquiry_title: { title : "タイトル" },
        note: { title : "お問い合わせ内容" },
        reason_note: { title : "対応内容" },
        toDepartment: { title : "アポロ管財依頼先部署" },
        toStaffName: { title : "担当者" },
        byUser: { title : "依頼主" },
        requestDate: { title : "依頼日" },
        limitDate: { title : "期限" },
      },
      mainType: 'apollo_request_post',
      columns: [
        { title: '', class: '' },
        { title: '', class: '' },
        { title: 'お問い合わせカテゴリー', class: 'text-left', item: 'category' },
        { title: 'タイトル / デンタツ問い合わせ内容', class: 'text-left', item: 'inquiry_title' },
        { title: 'お客様会社名', class: 'text-left', item: 'clientName' },
        { title: 'お客様担当者', class: 'text-left', item: 'clientPersonName' },
        { title: '担当者', class: 'text-left', item: 'toStaffName' },
        { title: '期限', class: '', item: 'limitDate' },
        { title: '' },
        { title: '' },
        { title: '' },
      ],
    }
  },

  computed: {
    /**
     * 取得した一覧データ
     */
    list() {
      // 過去に取得したデータをそのまま取得（stateでOKなぜなら取得し終わったらこの画面で空に変更するから）
      return this.$store.state.mainList
    },

    /**
     * ログインユーザー名そのまま
     */
    userNameWithKana() {
      return this.$store.getters.user.user_name
    },

    /**
     * ログインユーザー名カナ削除
     */
    userName() {
      return utils.deleteKana(this.$store.getters.user.user_name)
    },

    /**
     * ログインユーザー名カナ削除・スペース削除
     */
    userNameRemoveSpace() {
      return utils.deleteSpace(utils.deleteKana(this.userName))
    },

    /**
     * ログインユーザーID
     */
    userId() {
      return this.$store.getters.user.id
    },

    /**
     * メインの所属部署名
     */
    mainDepartment() {
      const res = this.$store.getters.loginUserData
      return res.departmentName
    },

    /**
     * 所属部署（兼任含む）
     */
    dualPosition() {
      return this.$store.getters.dualPosition
    },

    /**
     * 所属部署名のみの配列
     */
    dualPositionNames() {
      if (this.dualPosition && this.dualPosition.length) {
        const res = this.dualPosition.map(row => row.departmentName)
        if (res && res.length) {
          return res
        }
      }
      return []
    },

    /**
     * 所属部署IDのみの配列
     */
    dualPositionIds() {
      if (this.dualPosition && this.dualPosition.length) {
        const res = this.dualPosition.map(row => row.departmentId)
        if (res && res.length) {
          return res
        }
      }
      return []
    },

    /**
     * 伝達部署
     */
    transDepartmentList() {
      return this.$store.getters.transDepartmentList
    },

    /**
     * 権限
     */
    auth() {
      return this.$store.getters.auth
    },

    /**
     * 管理者権限あり
     */
    admin() {
      return utils.admin(this.auth)
    },

    /**
     * 社員面接権限あり
     */
    regularStaffAuth() {
      if (this.auth.regularStaffAuth) {
        return true
      }
      return false
    },

    /**
     * 人事総務経理所属か否か
     */
    manager() {
      return utils.manager(this.dualPosition)
    },

    isSmallDevice() {
      return (utils.deviceInfo.isMobile || utils.deviceInfo.isTablet)
    },
  },

  created() {
    this.freeze = true
    const departmentList = this.$store.getters.departmentList
    this.toDepartmentList = [ ...departmentList, ...this.transDepartmentList ]
    this.getRegularStaffAuthorized()
  },

  mounted() {
    this.$nextTick(() => {
      this.freeze = false
      this.loadCondition()
      this.refresh()
    })
  },

  methods: {
    /**
     * データ取得
     */
    async refresh(next) {
      // 詳細画面から戻った初回の場合
      const lastKeyItem = sessionStorage.getItem(stLast)
      let lastKey
      if (lastKeyItem) {
        lastKey = JSON.parse(lastKeyItem)
      }

      // メインタスク閲覧から戻ってきた場合
      if (this.firstRefresh && lastKey && this.$route.query.main && this.$route.query.main == true && this.list && this.list.length) {
        // 先ほどのデータを表示
        this.searchResults = utils.clone(this.list)
        this.last = lastKey

        // カーソル
        await utils.wait(200)
        const scrollY = this.$store.state.mainListScroll
        if (scrollY) {
          // 画面復帰時にスクロール位置を戻す
          this.$nextTick(() => {
            // this.$refs.requestPostList.scrollTop = scrollY
            this.$refs.requestPostList.scrollTo({
              top: scrollY,
              behavior: 'smooth'
            })
          })
        }

        this.$store.dispatch('setMainList', [])
        this.$store.dispatch('setMainListScroll', null)
      } else {
        // 検索結果がゼロになる場合
        if (!this.toggle.mainTask && !this.toggle.orderTask && !this.toggle.employmentTask && !this.toggle.employmentRegularTask) {
          await dialogs.showErrorDialog('トグルの表示が全てOFF', '「メインタスク」などの表示が全てOFFになっています。どれかONにしてください。')
          return
        }
        // 担当者が複数
        if (this.btn.user && this.text.toStaffName) {
          await dialogs.showErrorDialog('担当分がON、かつ担当者検索あり', `担当者検索が自分の担当分、かつ「${this.text.toStaffName}」も検索になっています。どちらか一方にしてください。`)
          return
        }
        // 依頼主が複数
        if (this.btn.byUser && this.text.byUser) {
          await dialogs.showErrorDialog('依頼分がON、かつ依頼主検索あり', `依頼主検索が自分の依頼、かつ「${this.text.byUser}」も検索になっています。どちらか一方にしてください。`)
          return
        }
        await this.fetchRequestPosts(next)
      }
      this.firstRefresh = false
    },

    /**
     * 下までスクロールした場合、次の50件を取得
     */
    onScroll:  _.throttle(async function() {
      // データ取得中
      if (this.loading) {
        return
      }
      const el = this.$refs.requestPostList
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {

        // データがない場合
        if (this.last.scrollEnd) {
          console.log('次のデータなし')
          return
        }
        // しおりがない場合
        if (!this.last.requestNo) {
          console.log('しおりなし')
          return
        }
        this.loading = true
        console.log('次の50件を取得')
        console.log(this.last)
        await this.refresh(this.last)
      }
    }, 200),

    /**
     * 検索
     */
    async fetchRequestPosts(next) {
      if (!next) {
        this.searchResults = []
      }

      // 雇い入れ社員は、グループ4以上なら同じ部署のみOK
      const group4 = utils.isGroup4(this.$store.getters.user.group_num) // G4以上か否か

      this.loading = true
      const resList = await requestPostManager.searchRequestPosts(
        this,
        searchKeys,
        this.btn,
        this.toggle,
        this.text,
        this.userName,
        this.userNameWithKana,
        this.userId,
        group4,
        this.admin,
        this.regularStaffAuth,
        this.manager,
        this.mainDepartment,
        this.dualPositionNames,
        this.dualPositionIds,
        next
      )
      const list = resList.list
      console.log(list.length)

      this.searchResults = this.searchResults.concat(list)
      console.log('総件数：' + this.searchResults.length)

      this.loading = false

      // ページングのために最後の行の項目を保存
      if (list && list.length) {
        const res = resList.lastData
        this.last = {
          scrollEnd: false,
          [this.text.sortItem]: res[this.text.sortItem],
          requestNo: res.requestNo
        }
        this.last.createDate = res.createDate
        console.log('最終行キー項目:::')
        console.log(this.last)
      } else {
        this.last.scrollEnd = true
      }
      this.setSessionStorage(stLast, this.last)
    },

    /**
     * 複写
     * @param id
     */
    toCopyMain(id) {
      this.$router.push({ name: 'RequestPost', query: { requestPostId: id, mode: '3' }})
    },

    /**
     * 編集
     * @param row
     */
    toEditMain(row) {
      // メインタスク画面から戻ってきたときのために一覧を保存
      this.$store.dispatch('setMainList', this.searchResults)
      this.$store.dispatch('setMainListScroll', this.$refs.requestPostList.scrollTop)
      // console.log(this.$refs.requestPostList.scrollTop)

      if (row.type == this.mainType) {
        this.$router.push({ name: 'RequestPost', query: { mode: 1, requestPostId: row._id }})
      } else if (row.type == 'order') {
        this.$router.push({ name: 'RequestPost', query: { mode: 6, orderId: row._id }})
      } else if (row.type == 'task_request') {
        this.$router.push({ name: 'RequestPost', query: { mode: 1, requestPostId: row.requestPostId }})
      } else if (row.type == 'task_request_order') {
        this.$router.push({ name: 'RequestPost', query: { mode: 6, orderId: row.orderId }})
      }
    },

    /**
     * 依頼登録
     * @param row
     */
    toSub(id, type) {
      if (type == this.mainType) {
        this.$router.push({ name: 'TaskRequest', query: { requestPostId: id }})
      } else {
        this.$router.push({ name: 'TaskRequest', query: { orderId: id, mode: '1' }})
      }
    },

    /**
     * 本日までの期限ボタン 押下
     */
    untilToday() {
      this.btn.untilToday = !this.btn.untilToday
      if (this.btn.untilToday) {
        this.btn.incomplete = true
      }
      this.reload++
    },

    /**
     * 未完了のみボタン
     */
    incompBtn() {
      this.btn.incomplete = !this.btn.incomplete
      this.reload++
    },

    /**
     * カラムクリックでソート変更
     */
    sortSwitching(tar) {
      if (this.loading) {
        return
      }
      if (!tar.item) {
        return
      }
      // 同じカラムを再度クリック
      if (tar.item == this.text.sortItem) {
        if (this.text.sortBy == 1) {
          this.text.sortBy = -1
        } else {
          // 初期値に戻す
          this.text.sortItem = 'createDate'
        }
      } else {
        this.text.sortItem = tar.item
        this.text.sortBy = 1
      }
      this.refresh()
    },

    /**
     * ソートの説明
     * @param tar 対象のカラム
     */
    sortMsg(tar) {
      if (this.loading || !tar.title) {
        return ''
      }
      // 同じカラムを再度
      if (tar.item == this.text.sortItem) {
        if (this.text.sortBy == 1) {
          return 'クリックすると「' + tar.title + '」を降順で検索します'
        } else {
          // 初期値に戻す
          return 'クリックすると' + '初期値（作成日の降順）のソートに戻して検索します'
        }
      } else {
        return 'クリックすると「' + tar.title + '」を昇順で検索します'
      }
    },

    /**
     * お問い合わせNo表示
     */
    requestNo(row) {
      if (row.requestNo) {
        return 'お問い合わせNo. ' + row.requestNo
      }
      return ''
    },

    /**
     * 完了できるか確認
     */
    async checkCanCompelte(data) {
      // 管理者権限はOK
      const auth = this.$store.getters.auth
      if (auth.adminAuth) {
        return true
      }

      const name = this.userNameRemoveSpace

      // ログインユーザーが担当者の場合、OK
      const tanto = utils.deleteSpace(utils.deleteKana(data.toStaffName))
      if (name == tanto) {
        return true
      }

      // ログインユーザーが依頼者の場合、OK
      const irai = utils.deleteSpace(utils.deleteKana(data.byUser))
      if (name == irai) {
        return true
      }

      // 担当者に関係なく、同じ部署の人はOK
      // 兼任を含めたログインユーザーの所属部署
      for (let i = 0; i < this.dualPosition.length; i++) {
        const tar = this.dualPosition[i]
        if (tar.departmentName == data.toDepartment || tar.departmentId == data.toDepartmentId) {
          return true
        }
      }
      // デンタツの場合
      // 該当のデンタツ部署のデータを取得
      let dentatsu = this.transDepartmentList.find((r) => {
        return (r.name == data.toDepartment || r.departmentId == data.toDepartmentId)
      })
      // 該当のデンタツに紐づく部署の人はOK（RLS_デンタツならRLS全員OK）
      if (dentatsu && dentatsu.transDepartment) {
        for (let i = 0; i < this.dualPosition.length; i++) {
          const tar = this.dualPosition[i]
          if (tar.departmentId == dentatsu.transDepartment) {
            return true
          }
        }
      }
      await dialogs.showErrorDialog('完了できません', '担当者と同じ部署・依頼主でないため、完了できません。')
      return false
    },

    /**
    * 未完了ボタンを押下するとタスクを完了に変更
    */
    async changeStatus(row) {
      if (this.saving) {
        return
      }
      // メインタスクの場合のみ
      if (![this.mainType, 'order'].includes(row.type)) {
        return
      }

      // すでに完了済みの場合
      if (row.status == 'done') {
        return
      }

      // 同行の場合、手動で完了は不可
      if (row.accompanyId) {
        await dialogs.showErrorDialog('変更不可', '同行報告書と紐づいているため、手動で変更できません。')
        return
      }

      const auth = await this.checkCanCompelte(row)
      if (!auth) {
        return
      }

      const dialogResult = await dialogs.showConfirmDialog('タスクの完了（完了後は編集不可）', 'タスクを完了すると編集できなくなります。完了してよろしいですか？', 'はい', 'キャンセル')
      if (dialogResult === 'YES') {
        this.saving = true
        // 紐づくサブタスク
        let subTasks = []
        // メインタスク名
        let mainName = 'メインタスク'

        // メインタスクの場合
        if (row.type == this.mainType) {
          // IDを元にリクエストポストに紐づく、サブタスクを検索
          subTasks = await taskRequestManager.getByRequestPostId(this, row._id, null)

        // 受注タスクの場合
        } else if (row.type == 'order') {
          mainName = '受注タスク'
          // IDを元に受注に紐づく、サブタスクを検索
          subTasks = await taskRequestManager.getByOrderId(this, row._id, null)
        }

        // サブタスクが全てdoneになっているか
        const statusResult = subTasks.every(function(val) {
          return val.status == 'done'
        })

        // trueの場合、メインタスクのステータスをdoneにする
        if (statusResult) {
          let result

          // メインタスクの場合
          if (row.type == this.mainType) {
            result = await requestPostManager.markAsDone(this, row._id)

            // DC面接の場合、採用であれば雇い入れタスク登録
            await requestPostManager.autoEmployTaskSave(this, row)
          // 受注タスクの場合
          } else {
            result = await orderManager.markAsDone(this, row._id)
            if (result) {
              // 見積りのデータの受注ステータス更新、完了にした人を保存
              await estimateManager.changeOrderStatus(this, row.estimateNo, '受注完了', this.userName)
            }
          }
          
          if (result) {
            this.saving = false
            await dialogs.showSuccessDialog('タスクの完了', '完了にしました。')
            row.status = 'done'
            row.statusText = '完了'
            row.iconName = 'CheckCircle'
            row.iconColor = 'green'
            row.color = 'bg-blue-100'

            logManager.recording(this, logManager.Loglevel.INFO, mainName, 'ステータス完了', mainName + '（お問い合わせ番号：' + row.requestNo + '）を完了しました。')
          } else {
            this.saving = false
            await dialogs.showErrorDialog('エラー', '完了にできませんでした。')
          }
        } else {
          this.saving = false
          await dialogs.showErrorDialog('サブタスク未完了', 'サブタスクを全て完了してから、再度行ってください。')
          return
        }
      }
      this.saving = false
    },

    /**
     * ソート change イベント
     */
    async changeSort(data) {
      this.text.sortItem = data.item
      this.text.sortBy = data.by
      // セッションストレージに保存
      this.setSessionStorage(stText, this.text)
      this.refresh()
    },

    /**
     * 検索ボタン 押下
     */
    search() {
      this.last.scrollEnd = false
      // セッションストレージに検索条件保存
      this.setSessionStorage(stBtn, this.btn)
      this.setSessionStorage(stToggle, this.toggle)
      this.setSessionStorage(stText, this.text)
      this.refresh()
    },

    /**
     * 見積りから遷移した場合の検索
     */
    fromEstimateView() {
      // 見積参照から遷移した場合
      if (this.$route.query.requestNo) {
        // セッションストレージ削除
        this.removeSettion()
        // 問い合わせNoで検索をかける
        this.text.searchKeyword = utils.clone(this.$route.query.requestNo)

        // 担当者、依頼主絞り込み解除
        this.btn.user = false
        this.btn.byUser = false

        // 完了のみをOFF
        this.btn.incomplete = false

        // メインタスクをON
        this.toggle.mainTask = true
        // 受注タスクをON
        this.toggle.orderTask = true
      }
    },

    /**
     * 社員雇い入れ切り替えタブを表示
     */
    async getRegularStaffAuthorized() {
      // 管理者権限もしくは人事経理総務
      const isEmployment = (this.admin || this.manager)
      // グループ4以上なら同じ部署のみOK
      const group4 = utils.isGroup4(this.$store.getters.user.group_num)
      if (isEmployment || group4) {
        this.isRegularStaffAuthorized = true
      } else {
        this.isRegularStaffAuthorized = false
      }
    },

    /**
     * 検索条件をクリア
     */
    clear() {
      this.removeSettion()
      this.$router.go({path: this.$router.currentRoute.path, force: true})
    },

    /**
     * セッションストレージ削除
     */
    removeSettion() {
      sessionStorage.removeItem(stBtn)
      sessionStorage.removeItem(stToggle)
      sessionStorage.removeItem(stText)
      sessionStorage.removeItem(stLast)
    },

    /**
     * 検索条件をセッションストレージへ保存
     */
    setSessionStorage(name, searchCondition) {
      const searchStr = JSON.stringify(searchCondition)
      sessionStorage.setItem(name, searchStr)
    },

    /**
     * セッションストレージから検索条件を取得
     */
    loadCondition() {
      // 見積りから遷移した場合の検索設定
      if (this.$route.query.requestNo) {
        this.fromEstimateView()
      } else {
        const btn = sessionStorage.getItem(stBtn)
        const toggle = sessionStorage.getItem(stToggle)
        const text = sessionStorage.getItem(stText)

        if (btn) {
          this.btn = JSON.parse(btn)
        }

        if (toggle) {
          this.toggle = JSON.parse(toggle)
        }

        if (text) {
          this.text = JSON.parse(text)
          if (this.text.searchKeyword) {
            this.keywordOpen = true
          }
          // カレンダーに反映
          if (this.text.limitPeriod && this.text.limitPeriod.start) {
            this.$refs.dateSelect.setRange(this.text.limitPeriod, this.text.limitPeriodName, true)
          }
        }
      }
    },

    /**
     * キーワード検索クリック
     */
    clickKeword() {
      if (this.text.searchKeyword) {
        this.keywordOpen = true
      } else {
        this.keywordOpen = !this.keywordOpen
      }
    },

    /**
     * 期限（メイン）検索 変更
     */
    periodChange(range, presetName) {
      this.text.limitPeriod = range
      this.text.limitPeriodName = presetName
    },
  },
}
</script>

<style scoped>
  .sug >>> input[type=text] {
    height: 39px;
    margin-top: -2px;
    border-radius: 0.375rem !important;
    width: 150px !important;
  }

  .btn-form {
    min-width: 779px;
  }

  .list {
    min-width: 1380px;
  }

  .table-width {
    min-width: 1310px;
  }

  .t-thead {
    z-index: 20;
    background: #ebf0fb;
  }

  .row {
    padding: 10px 6px;
  }

  .row-limit {
    padding: 5px 6px;
  }

  .blue-text {
    color: blue;
    font-weight: bold;
  }

  .hover-color:hover {
    background: rgba(225, 244, 247, 0.67) !important;
    transform: translateY(-2px);        /* 上に 2px 浮かせる */
    box-shadow: 0 2px 2px rgba(0,0,0,0.15); /* 影をつける */
    font-weight: bold;
  }

  .title::placeholder {
    font-size: 12px;
  }

  .title::-webkit-input-placeholder {
    font-size: 12px;
  }

  .keyword::placeholder {
    font-size: 12px;
  }

  .keyword::-webkit-input-placeholder {
    font-size: 12px;
  }

  .min-w-135px {
    min-width: 135px;
  }

  .yellow >>> input[type=text] {
    background-color: #efdf8a !important;
    font-weight: bold !important;
  }

  .filter-date >>> div {
    color: rgb(70, 70, 70) !important;
  }

  .filter-date >>> input[type=text] {
    background: white !important;
    font-size: small !important;
  }

  .yellow >>> input[type=text] {
    background-color: #efdf8a !important;
    font-weight: bold !important;
  }

  /* 期限区分 */
  .gray-limit {
    border-radius: 10px;
    background-color: rgb(150, 147, 147);
    color: white;
    padding: 0px 8px;
    font-size: 10px;
    font-weight: bold;
    width: fit-content;
    white-space: nowrap;
    margin: auto;
  }

  .white-limit {
    border-radius: 10px;
    background-color: whitesmoke;
    border: 1px solid gray;
    padding: 0px 8px;
    font-size: 10px;
    font-weight: bold;
    width: fit-content;
    white-space: nowrap;
    margin: auto;
    color: gray;
  }

  .allow-none {
    appearance: none !important;
    -webkit-appearance: none !important;
    background-image: none;
  }
</style>